import type {
  GeneratedImageDisplay,
  GeneratedSvgDisplay,
  InlineChartDisplay,
  VoiceReportEmbedDisplay,
} from "@ando/core/domain";
import { Chart, cn } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { BarChart3, FileBarChart, Image as ImageIcon, Shapes, Table } from "lucide-react";
import type * as React from "react";
import { ResultChip } from "../../structured-data/components/ResultChip";
import { inlineChartToRender } from "../../structured-data/inlineChartToOption";
import { pluralizeRows } from "../../structured-data/utils/pluralizeRows";
import { generatedImageDocId, generatedSvgDocId } from "../generatedMedia";

/**
 * MessagePreviewEmbed — single polymorphic preview surface for the agent-
 * generated media kinds rendered just below the agent bubble in the chat.
 *
 * Replaces the prior tiny `MediaChip` (a flat label) — preview embeds signal
 * "I made this for you" far more strongly than a chip the user can miss. One
 * component handles all three kinds (chart / image / svg) via a discriminated
 * union so the outer button shell, hover/focus chrome, motion, and the right-
 * hand text column stay DRY across kinds (June 14, 2026).
 *
 * The chart branch reuses the converter + `<Chart>` wrapper the dashboard +
 * inline panel already use — zero new render logic. The parent owns its own
 * skeleton state, so `suppressSuspenseFallback` is on for the embedded chart.
 */
export type MessagePreviewEmbedProps =
  | {
      readonly kind: "chart";
      readonly payload: InlineChartDisplay;
      readonly onOpen: () => void;
    }
  | {
      readonly kind: "image";
      readonly payload: GeneratedImageDisplay;
      readonly onOpen: () => void;
    }
  | {
      readonly kind: "svg";
      readonly payload: GeneratedSvgDisplay;
      readonly onOpen: () => void;
    }
  | {
      readonly kind: "voice_report";
      readonly payload: VoiceReportEmbedDisplay;
      readonly onOpen: () => void;
    };

// ---------------------------------------------------------------------------
// Internal — shared card chrome reused by both layout variants (side-by-side
// for image/SVG with thumbnail, vertical full-width for chart embed). DRY:
// a future visual refresh (radius, hover lift, focus ring) is a one-line
// change instead of two synchronised inline classNames.
// ---------------------------------------------------------------------------

const CARD_FRAME_CHROME = cn(
  "group relative rounded-xl border border-hairline bg-white/55 text-left transition-all",
  "hover:border-ando-orange/40 hover:bg-white hover:shadow-sm",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
  "motion-safe:animate-msg-in",
);

// Side-by-side layout for thumbnail-style media (image, SVG): the asset
// is a fixed-size square on the left, text column on the right.
const SIDE_BY_SIDE_LAYOUT = "flex items-stretch gap-3 p-2.5";

// Full-width vertical layout for the chart embed: ECharts needs the full
// width of the bubble + a comfortable 200px of height to fit axis labels,
// legend, and bars without overlap (June 14, 2026 fix — the previous
// 280×120 side-by-side variant starved the chart of pixels).
const VERTICAL_CARD_LAYOUT = "flex w-full max-w-[480px] flex-col gap-2 p-3";

interface EmbedFrameProps {
  readonly ariaLabel: string;
  readonly onOpen: () => void;
  readonly label: string;
  readonly icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  readonly title: string;
  readonly children: React.ReactNode;
}

const EmbedFrame: React.FC<EmbedFrameProps> = ({
  ariaLabel,
  onOpen,
  label,
  icon: Icon,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={ariaLabel}
    className={cn(CARD_FRAME_CHROME, SIDE_BY_SIDE_LAYOUT)}
  >
    {children}
    <div className="flex min-w-0 flex-col justify-center">
      <p className="flex items-center gap-1 font-medium text-[10px] text-ando-orange uppercase tracking-wider">
        <Icon size={10} strokeWidth={2} aria-hidden />
        <span>{label}</span>
      </p>
      <p className="mt-0.5 max-w-[180px] truncate font-medium text-[12px] text-ink-1">{title}</p>
      <span className="mt-1 text-[10px] text-ink-3">
        {i18nSingleton._(msg`Apri per esplorare →`)}
      </span>
    </div>
  </button>
);
EmbedFrame.displayName = "EmbedFrame";

// ---------------------------------------------------------------------------
// Public — one polymorphic component, three branches.
// ---------------------------------------------------------------------------

export const MessagePreviewEmbed: React.FC<MessagePreviewEmbedProps> = (props) => {
  if (props.kind === "chart") {
    const result = inlineChartToRender(props.payload);
    if (result.kind === "empty") return null;

    // Tabular payloads (BE ``table`` / ``aggregation``) degrade to a chip —
    // an HTML ``<table>`` inside a 200px embed always overflows visually,
    // and the work-area panel renders the same data full-size on click.
    // The chip mirrors ``DisplayResultChips`` chrome so a future visual
    // refresh changes one component, not two (DRY).
    if (result.type === "table") {
      return (
        <TablePreviewChip
          rowCount={result.data.length}
          title={result.title}
          onOpen={props.onOpen}
        />
      );
    }

    // Real chart kinds — full-width vertical embed so ECharts has room
    // for axis labels + legend + bars without overlap. The earlier
    // 280×120 side-by-side layout starved the chart of pixels (June 14
    // 2026 user feedback: "i grafici non si vedono bene!").
    return (
      <button
        type="button"
        onClick={props.onOpen}
        aria-label={i18nSingleton._(msg`Apri il grafico: ${result.title}`)}
        className={cn(CARD_FRAME_CHROME, VERTICAL_CARD_LAYOUT)}
      >
        <header className="flex flex-col gap-0.5">
          <p className="flex items-center gap-1 font-medium text-[10px] text-ando-orange uppercase tracking-wider">
            <BarChart3 size={10} strokeWidth={2} aria-hidden />
            <span>{i18nSingleton._(msg`Grafico generato`)}</span>
          </p>
          <p className="truncate font-medium text-[12px] text-ink-1">{result.title}</p>
        </header>
        <div className="h-[200px] w-full">
          <Chart
            type={result.type}
            data={result.data}
            columns={result.columns}
            viz={result.viz}
            title={result.title}
            suppressSuspenseFallback
          />
        </div>
        <span className="text-[10px] text-ink-3">{i18nSingleton._(msg`Apri per esplorare →`)}</span>
      </button>
    );
  }

  if (props.kind === "voice_report") {
    // Voice-of-X report embed (Workstream B M2.4, June 17, 2026).
    //
    // Distinct from ``chart`` / ``image`` / ``svg`` because the underlying
    // artifact is a multi-section structured document: collapsing it to a
    // chip would hide the three highest-signal facts the user uses to
    // decide whether to navigate (title, category count, quality score,
    // exec summary). We render the side-by-side card chrome (DRY via
    // ``EmbedFrame``) but swap the right-hand text column for a richer
    // two-row meta block — the asset is conceptually closer to a "result
    // doc preview" than a media thumbnail.
    const score = Math.round(props.payload.final_score * 100);
    return (
      <button
        type="button"
        onClick={props.onOpen}
        aria-label={i18nSingleton._(msg`Apri il report Voice of X: ${props.payload.title}`)}
        className={cn(CARD_FRAME_CHROME, SIDE_BY_SIDE_LAYOUT, "max-w-[320px]")}
      >
        <div
          className="grid h-16 w-16 flex-shrink-0 place-items-center rounded-lg bg-ando-orange-soft/40"
          aria-hidden
        >
          <FileBarChart size={28} strokeWidth={1.75} className="text-ando-orange" />
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <p className="flex items-center gap-1 font-medium text-[10px] text-ando-orange uppercase tracking-wider">
            <FileBarChart size={10} strokeWidth={2} aria-hidden />
            <span>{i18nSingleton._(msg`Voice of X`)}</span>
          </p>
          <p className="mt-0.5 truncate font-medium text-[12px] text-ink-1">
            {props.payload.title}
          </p>
          <p className="mt-0.5 text-[10.5px] text-ink-3 tabular-nums">
            {i18nSingleton._(
              msg`${props.payload.n_categories} categorie · ${score}%`,
            )}
          </p>
          {props.payload.executive_summary ? (
            <p className="mt-1 line-clamp-2 text-[11px] text-ink-2 leading-snug">
              {props.payload.executive_summary}
            </p>
          ) : null}
        </div>
      </button>
    );
  }

  if (props.kind === "image") {
    const docId = generatedImageDocId(props.payload);
    if (!docId) return null;
    const url = props.payload.image_url;
    if (!url) return null;
    const title = props.payload.prompt?.trim() || i18nSingleton._(msg`Immagine generata`);
    return (
      <EmbedFrame
        ariaLabel={i18nSingleton._(msg`Apri l'immagine: ${title}`)}
        onOpen={props.onOpen}
        label={i18nSingleton._(msg`Immagine generata`)}
        icon={ImageIcon}
        title={title}
      >
        <img
          src={url}
          alt={title}
          loading="lazy"
          className="h-24 w-24 flex-shrink-0 rounded-lg object-cover"
        />
      </EmbedFrame>
    );
  }

  // svg branch — last by elimination on the union.
  const docId = generatedSvgDocId(props.payload);
  if (!docId) return null;
  const url = props.payload.svg_url;
  if (!url) return null;
  const title = props.payload.prompt?.trim() || i18nSingleton._(msg`Illustrazione generata`);
  return (
    <EmbedFrame
      ariaLabel={i18nSingleton._(msg`Apri l'illustrazione: ${title}`)}
      onOpen={props.onOpen}
      label={i18nSingleton._(msg`SVG generato`)}
      icon={Shapes}
      title={title}
    >
      <img
        src={url}
        alt={title}
        loading="lazy"
        className="h-24 w-24 flex-shrink-0 rounded-lg object-contain"
      />
    </EmbedFrame>
  );
};
MessagePreviewEmbed.displayName = "MessagePreviewEmbed";

// ---------------------------------------------------------------------------
// TablePreviewChip — compact re-entry for ``table`` / ``aggregation`` payloads.
// ---------------------------------------------------------------------------
//
// The BE emits ``aggregation`` + ``table`` displays as opaque row sets (no
// chart). Embedding an HTML ``<table>`` at 200px h either truncates the rows
// or scrolls inside the bubble — both feel broken. We degrade to the shared
// ``<ResultChip>`` primitive so this surface and ``DisplayResultChips`` share
// one chrome (DRY — June 15, 2026 audit) and a future visual refresh changes
// one file, not two. It keeps the shared ``OpensInPanel`` arrow: standing
// alone under a message, this chip IS the affordance, so the cue earns its
// place (it was suppressed while the hint was a hover-only external-link
// glyph, which promised the wrong destination anyway).

interface TablePreviewChipProps {
  readonly rowCount: number;
  readonly title: string;
  readonly onOpen: () => void;
}

const TablePreviewChip: React.FC<TablePreviewChipProps> = ({ rowCount, title, onOpen }) => (
  <ResultChip
    icon={Table}
    label={i18nSingleton._(msg`Tabella generata`)}
    subtitle={pluralizeRows(rowCount)}
    ariaLabel={i18nSingleton._(msg`Apri la tabella: ${title}`)}
    onClick={onOpen}
  />
);
TablePreviewChip.displayName = "TablePreviewChip";
